import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { 
  Upload, 
  Users, 
  FileText, 
  LogOut, 
  Trash2, 
  AlertCircle,
  CheckCircle2,
  FileUp,
  RefreshCw,
  Save,
  X,
  Eye,
  Search,
  Stethoscope,
  Loader2,
  Calendar,
  HardDrive,
  Database,
  BarChart3,
  TrendingUp,
  Megaphone,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileSpreadsheet,
  PenLine,
  Pencil,
  ShieldOff,
  GraduationCap,
  Plane,
  CalendarClock,
  BadgeCheck,
  MessageSquare,
  ClipboardList,
  Clock,
  ShieldCheck,
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  LayoutGrid,
  Download,
  UserCog,
  Radio,
  UserMinus,
  BookOpen,
  UserSquare2,
  ImageOff,
  ImageIcon,
  MapPin,
  FileCheck,
  IdCard,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AdminCoursesTab from "../components/AdminCoursesTab";
import AdminFolhaPontoTab from "../components/AdminFolhaPontoTab";
import AdminCentralDemissionalTab from "../components/AdminCentralDemissionalTab";
import AdminVacationNoticesTab from "../components/AdminVacationNoticesTab";
import AdminPostosView from "../components/AdminPostosView";
import AdminContratosView from "../components/AdminContratosView";
import AdminVacationAdvancesTab from "../components/AdminVacationAdvancesTab";
import AdminAdmissionKitsTab from "../components/AdminAdmissionKitsTab";
import ImportRosterDialog from "../components/ImportRosterDialog";
import AdminWhatsappBroadcast from "../components/AdminWhatsappBroadcast";
import AdminZapiHealth from "../components/AdminZapiHealth";
import AdminMessageTemplatesTab from "../components/AdminMessageTemplatesTab";
import AdminFormsTab from "../components/AdminFormsTab";
import AdminAfdTab from "../components/AdminAfdTab";
import AdminOpsSupervisorsTab from "../components/AdminOpsSupervisorsTab";
import EditEmployeeDialog from "../components/EditEmployeeDialog";
import BulkDeleteEmployeesDialog from "../components/BulkDeleteEmployeesDialog";
import AdminAsoUploadTab from "../components/AdminAsoUploadTab";
import AdminCodigoEticaTab from "../components/AdminCodigoEticaTab";
import AdminBannersPanel from "../components/AdminBannersPanel";
import AdminVideosPanel from "../components/AdminVideosPanel";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, employee: null });
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importRosterOpen, setImportRosterOpen] = useState(false);
  const [editEmployeeId, setEditEmployeeId] = useState(null);
  const [createEmployeeOpen, setCreateEmployeeOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const perPage = 50;
  
  // Sorting state
  const [sortField, setSortField] = useState("nome"); // "nome" or "access_count"
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"
  
  // Upload form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMonth, setUploadMonth] = useState("");
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear().toString());
  const [uploadTipo, setUploadTipo] = useState("normal");
  const [dragActive, setDragActive] = useState(false);
  
  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  
  // Progress state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState(""); // "uploading", "processing", "completed"
  const [progressMessage, setProgressMessage] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const progressIntervalRef = useRef(null);
  
  // Period management state
  const [periods, setPeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [deletePeriodDialog, setDeletePeriodDialog] = useState({ open: false, period: null });
  const [deletingPeriod, setDeletingPeriod] = useState(false);
  
  // Storage info state
  const [storageInfo, setStorageInfo] = useState({
    used_mb: 0,
    total_mb: 10240,
    remaining_mb: 10240,
    used_percent: 0
  });
  const [loadingStorage, setLoadingStorage] = useState(false);

  // Access stats state
  const [accessStats, setAccessStats] = useState({
    total_accesses: 0,
    unique_employees: 0,
    avg_per_day: 0,
    month_name: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    top_employees: []
  });
  const [loadingAccessStats, setLoadingAccessStats] = useState(false);
  const [accessStatsMonth, setAccessStatsMonth] = useState(new Date().getMonth() + 1);
  const [accessStatsYear, setAccessStatsYear] = useState(new Date().getFullYear());

  // Announcement state
  const [announcement, setAnnouncement] = useState({ text: '', active: false });
  const [announcementText, setAnnouncementText] = useState('');
  const [announcementActive, setAnnouncementActive] = useState(false);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);

  // Income Report state
  const [incomeReportFile, setIncomeReportFile] = useState(null);
  const [incomeReportUploading, setIncomeReportUploading] = useState(false);
  const [incomeReportPreview, setIncomeReportPreview] = useState(null);
  const [incomeReportSessionId, setIncomeReportSessionId] = useState(null);
  const [selectedIncomeReports, setSelectedIncomeReports] = useState([]);
  const [incomeReportSaving, setIncomeReportSaving] = useState(false);
  const [incomeReportYears, setIncomeReportYears] = useState([]);
  const [incomeReportStats, setIncomeReportStats] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    // Persist selected tab across refreshes so the admin doesn't lose
    // context when the app hot-reloads. Default is the new card menu.
    try {
      return localStorage.getItem("admin_active_tab") || "menu";
    } catch {
      return "menu";
    }
  });

  // Persist selected tab whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem("admin_active_tab", activeTab);
    } catch {
      /* localStorage disabled — ignore */
    }
  }, [activeTab]);

  // Tabs bar collapsed state (persisted). Starts collapsed so the top
  // navigation doesn't take two rows on every page — the admin can expand
  // it to jump between areas without going back to the menu.
  const [tabsBarOpen, setTabsBarOpen] = useState(() => {
    try {
      return localStorage.getItem("admin_tabs_bar_open") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("admin_tabs_bar_open", tabsBarOpen ? "1" : "0");
    } catch { /* ignore */ }
  }, [tabsBarOpen]);

  // Signature state
  const [signatureDialog, setSignatureDialog] = useState({ open: false, employee: null, image: null, loading: false });
  const [deleteSignatureDialog, setDeleteSignatureDialog] = useState({ open: false, employee: null });
  const [deletingSignature, setDeletingSignature] = useState(false);

  // Photo 3x4 state
  const [photoDialog, setPhotoDialog] = useState({ open: false, employee: null, image: null, loading: false, created_at: null });
  const [deletePhotoDialog, setDeletePhotoDialog] = useState({ open: false, employee: null });
  const [deletingPhoto, setDeletingPhoto] = useState(false);

  // Funcionários tab sub-view: "employees" | "postos" | "contratos"
  const [funcSubView, setFuncSubView] = useState("employees");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // Reset to page 1 on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchData = useCallback(async (page = 1, search = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString()
      });
      if (search) {
        params.append("search", search);
      }
      params.append("access_year", accessStatsYear);
      params.append("access_month", accessStatsMonth);
      params.append("sort_field", sortField);
      params.append("sort_order", sortOrder);
      
      const response = await axios.get(`${API}/admin/employees?${params}`, { timeout: 30000 });
      const data = response.data;
      
      setEmployees(data.employees);
      setTotalEmployees(data.total);
      setTotalPages(data.total_pages);
      setCurrentPage(data.page);
    } catch (error) {
      console.error("Error loading employees:", error);
      toast.error("Erro ao carregar dados. Clique em Atualizar.");
    } finally {
      setLoading(false);
    }
  }, [accessStatsYear, accessStatsMonth, sortField, sortOrder]);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder(field === "access_count" ? "desc" : "asc");
    }
    setCurrentPage(1);
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300" />;
    }
    return sortOrder === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  const fetchPeriods = useCallback(async () => {
    try {
      setLoadingPeriods(true);
      const response = await axios.get(`${API}/admin/payslip-periods`, { timeout: 30000 });
      setPeriods(response.data);
    } catch (error) {
      console.error("Error loading periods:", error);
    } finally {
      setLoadingPeriods(false);
    }
  }, []);

  const fetchStorageInfo = useCallback(async () => {
    try {
      setLoadingStorage(true);
      const response = await axios.get(`${API}/admin/storage-info`, { timeout: 30000 });
      setStorageInfo(response.data);
    } catch (error) {
      console.error("Error loading storage info:", error);
    } finally {
      setLoadingStorage(false);
    }
  }, []);

  const fetchAccessStats = useCallback(async (year, month) => {
    try {
      setLoadingAccessStats(true);
      const response = await axios.get(`${API}/admin/access-stats`, {
        params: { year, month },
        timeout: 30000
      });
      setAccessStats(response.data);
    } catch (error) {
      console.error("Error loading access stats:", error);
    } finally {
      setLoadingAccessStats(false);
    }
  }, []);

  const fetchAnnouncement = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/announcement`, { timeout: 30000 });
      setAnnouncement(response.data);
      setAnnouncementText(response.data.text || '');
      setAnnouncementActive(response.data.active || false);
    } catch (error) {
      console.error("Error loading announcement:", error);
    }
  }, []);

  const handleSaveAnnouncement = async () => {
    setSavingAnnouncement(true);
    try {
      await axios.post(`${API}/admin/announcement`, {
        text: announcementText,
        active: announcementActive
      });
      toast.success("Aviso atualizado com sucesso!");
      fetchAnnouncement();
    } catch (error) {
      toast.error("Erro ao salvar aviso");
    } finally {
      setSavingAnnouncement(false);
    }
  };

  // Income Report functions
  const fetchIncomeReportStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/income-report/list`);
      setIncomeReportStats(response.data);
    } catch (error) {
      console.error("Error fetching income report stats:", error);
    }
  }, []);

  const handleIncomeReportUpload = async () => {
    if (!incomeReportFile) return;
    
    setIncomeReportUploading(true);
    setIncomeReportPreview(null);
    
    try {
      const formData = new FormData();
      formData.append("file", incomeReportFile);
      
      toast.info("Processando PDF... Isso pode levar alguns minutos para arquivos grandes.");
      
      const response = await axios.post(`${API}/admin/income-report/preview`, formData, {
        timeout: 600000, // 10 minutes
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setIncomeReportPreview(response.data);
      setIncomeReportSessionId(response.data.session_id);
      
      // Select all valid employees by default
      const validCpfs = response.data.employees
        .filter(e => e.employee_exists)
        .map(e => e.cpf_clean);
      setSelectedIncomeReports(validCpfs);
      
      toast.success(`${response.data.employees_found} funcionários encontrados!`);
    } catch (error) {
      console.error("Error uploading income report:", error);
      toast.error("Erro ao processar PDF. Tente novamente.");
    } finally {
      setIncomeReportUploading(false);
    }
  };

  const handleSaveIncomeReports = async () => {
    if (!incomeReportSessionId || selectedIncomeReports.length === 0) return;
    
    setIncomeReportSaving(true);
    
    try {
      const response = await axios.post(`${API}/admin/income-report/save`, {
        session_id: incomeReportSessionId,
        selected_cpfs: selectedIncomeReports
      }, { timeout: 600000 });
      
      toast.success(response.data.message);
      setIncomeReportPreview(null);
      setIncomeReportFile(null);
      setIncomeReportSessionId(null);
      setSelectedIncomeReports([]);
      fetchIncomeReportStats();
    } catch (error) {
      console.error("Error saving income reports:", error);
      toast.error("Erro ao salvar informes");
    } finally {
      setIncomeReportSaving(false);
    }
  };

  const handleDeleteIncomeReportYear = async (ano) => {
    if (!window.confirm(`Tem certeza que deseja remover todos os informes de ${ano}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/income-report/${ano}`);
      toast.success(`Informes de ${ano} removidos!`);
      fetchIncomeReportStats();
    } catch (error) {
      toast.error("Erro ao remover informes");
    }
  };

  useEffect(() => {
    fetchData(currentPage, debouncedSearch);
    fetchPeriods();
    fetchStorageInfo();
    fetchAccessStats(accessStatsYear, accessStatsMonth);
    fetchAnnouncement();
    fetchIncomeReportStats();
  }, [currentPage, debouncedSearch, fetchData, fetchPeriods, fetchStorageInfo, fetchAccessStats, accessStatsYear, accessStatsMonth, fetchAnnouncement, fetchIncomeReportStats]);

  const handleDeletePeriod = async () => {
    if (!deletePeriodDialog.period) return;
    
    setDeletingPeriod(true);
    try {
      const { ano, mes } = deletePeriodDialog.period;
      await axios.delete(`${API}/admin/payslips/${ano}/${mes}`);
      toast.success(`Contracheques de ${deletePeriodDialog.period.mes_nome}/${ano} removidos!`);
      setDeletePeriodDialog({ open: false, period: null });
      fetchPeriods();
      fetchStorageInfo();
      fetchData(currentPage, debouncedSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao remover período");
    } finally {
      setDeletingPeriod(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === "application/pdf") {
        setSelectedFile(file);
        setPreviewData(null);
      } else {
        toast.error("Apenas arquivos PDF são aceitos");
      }
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setPreviewData(null);
      setUploadProgress(0);
      setUploadPhase("");
      setProgressMessage("");
    }
  };

  // Cleanup progress polling on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (saveProgressIntervalRef.current) {
        clearInterval(saveProgressIntervalRef.current);
      }
    };
  }, []);

  const pollProgress = async (sid) => {
    try {
      const response = await axios.get(`${API}/admin/upload-progress/${sid}`);
      const data = response.data;
      
      setUploadProgress(data.progress);
      setProgressMessage(data.message);
      
      if (data.status === "completed") {
        // Stop polling
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        
        // Fetch final result
        const resultResponse = await axios.get(`${API}/admin/upload-result/${sid}`);
        const resultData = resultResponse.data;
        
        if (resultData.success) {
          setPreviewData(resultData);
          setSelectedEmployees(resultData.employees.map(e => e.matricula));
          setUploadPhase("completed");
          toast.success(`${resultData.employees.length} funcionários encontrados!`);
        } else {
          toast.error(resultData.message || "Erro ao processar PDF");
        }
        
        // DON'T cleanup session here - needed for save operation
        // Session will be cleaned up after save or cancel
        setUploading(false);
        
      } else if (data.status === "error") {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        toast.error(data.message || "Erro ao processar PDF");
        setUploading(false);
        setUploadPhase("");
      }
    } catch (error) {
      console.error("Error polling progress:", error);
    }
  };

  const handlePreview = async () => {
    if (!selectedFile) {
      toast.error("Selecione um arquivo PDF");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadPhase("uploading");
    setProgressMessage("Enviando arquivo...");
    setPreviewData(null);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      // Use streaming upload for large files
      const response = await axios.post(`${API}/admin/preview-upload-stream`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: {
          mes: uploadMonth || undefined,
          ano: uploadYear || undefined,
          tipo: uploadTipo || "normal",
        },
        timeout: 60000, // 1 minute for initial upload
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          setProgressMessage(`Enviando arquivo... ${percentCompleted}%`);
        }
      });

      // Upload started, now poll for progress
      const sid = response.data.session_id;
      setSessionId(sid);
      setUploadPhase("processing");
      setUploadProgress(0);
      setProgressMessage("Processando PDF...");
      
      // Start polling for progress
      progressIntervalRef.current = setInterval(() => pollProgress(sid), 500);

    } catch (error) {
      console.error("Upload error:", error);
      setUploading(false);
      setUploadPhase("");
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("O upload está demorando muito. Verifique sua conexão.");
      } else if (error.response?.status === 413) {
        toast.error("Arquivo muito grande. Limite de 50MB.");
      } else if (!error.response) {
        toast.error("Erro de conexão. Verifique sua internet.");
      } else {
        toast.error(error.response?.data?.detail || "Erro ao enviar PDF");
      }
    }
  };

  // Save progress state
  const [saveSessionId, setSaveSessionId] = useState(null);
  const saveProgressIntervalRef = useRef(null);

  const pollSaveProgress = async (sid) => {
    try {
      const response = await axios.get(`${API}/admin/save-progress/${sid}`);
      const data = response.data;
      
      setUploadProgress(data.progress);
      setProgressMessage(data.message);
      
      if (data.status === "completed" || data.status === "error") {
        // Stop polling
        if (saveProgressIntervalRef.current) {
          clearInterval(saveProgressIntervalRef.current);
          saveProgressIntervalRef.current = null;
        }
        
        // Get final result
        const resultResponse = await axios.get(`${API}/admin/save-result/${sid}`);
        const resultData = resultResponse.data;
        
        if (resultData.success) {
          toast.success(`${resultData.employees_created} funcionários, ${resultData.payslips_created} contracheques salvos!`);
          setPreviewData(null);
          setSelectedFile(null);
          setSelectedEmployees([]);
          setSessionId(null);
          fetchData();
          fetchPeriods();
          fetchStorageInfo();
        } else {
          toast.error(resultData.message || "Erro ao salvar");
        }
        
        setSaving(false);
        setUploadPhase("");
      }
    } catch (error) {
      console.error("Error polling save progress:", error);
    }
  };

  const handleSaveEmployees = async () => {
    if (!previewData || selectedEmployees.length === 0) {
      toast.error("Selecione pelo menos um funcionário");
      return;
    }

    // Check if we have a session_id (new flow) or pdf_base64 (old flow)
    const hasSessionId = previewData.session_id || sessionId;
    
    if (hasSessionId) {
      // New optimized flow - save from session
      setSaving(true);
      setUploadPhase("saving");
      setUploadProgress(0);
      setProgressMessage("Iniciando salvamento...");
      
      try {
        const response = await axios.post(`${API}/admin/save-from-session`, {
          session_id: sessionId,
          selected_matriculas: selectedEmployees
        });
        
        const sid = response.data.save_session_id;
        setSaveSessionId(sid);
        
        // Start polling for save progress
        saveProgressIntervalRef.current = setInterval(() => pollSaveProgress(sid), 500);
        
      } catch (error) {
        console.error("Save error:", error);
        toast.error(error.response?.data?.detail || "Erro ao iniciar salvamento");
        setSaving(false);
        setUploadPhase("");
      }
    } else {
      // Old flow - employees have pdf_base64
      setSaving(true);

      try {
        const employeesToSave = previewData.employees.filter(e => 
          selectedEmployees.includes(e.matricula)
        );

        const response = await axios.post(`${API}/admin/save-employees`, {
          employees: employeesToSave
        }, {
          timeout: 300000 // 5 minutes for saving large batches
        });

        toast.success(`${response.data.employees_created} funcionários criados, ${response.data.payslips_created} contracheques salvos!`);
        
        setPreviewData(null);
        setSelectedFile(null);
        setSelectedEmployees([]);
        fetchData();
        fetchPeriods();
        fetchStorageInfo();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Erro ao salvar funcionários");
      } finally {
        setSaving(false);
      }
    }
  };

  const handleCancelPreview = () => {
    // Clean up session if exists
    if (sessionId) {
      axios.delete(`${API}/admin/upload-session/${sessionId}`).catch(() => {});
      setSessionId(null);
    }
    setPreviewData(null);
    setSelectedEmployees([]);
    setUploadPhase("");
  };

  const toggleEmployeeSelection = (matricula) => {
    setSelectedEmployees(prev => 
      prev.includes(matricula)
        ? prev.filter(m => m !== matricula)
        : [...prev, matricula]
    );
  };

  const toggleAllEmployees = () => {
    if (selectedEmployees.length === previewData?.employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(previewData?.employees.map(e => e.matricula) || []);
    }
  };

  // No client-side filtering needed - search is done on server
  const filteredEmployees = employees;

  const handleDeleteEmployee = async () => {
    if (!deleteDialog.employee) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/employees/${deleteDialog.employee.id}`);
      toast.success("Funcionário removido");
      setDeleteDialog({ open: false, employee: null });
      fetchData(currentPage, debouncedSearch);
    } catch (error) {
      toast.error("Erro ao remover funcionário");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAllEmployees = async () => {
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/admin/employees`);
      toast.success(`${response.data.employees_deleted} funcionários removidos`);
      setDeleteAllDialog(false);
      fetchData();
      fetchPeriods();
      fetchStorageInfo();
    } catch (error) {
      toast.error("Erro ao remover funcionários");
    } finally {
      setDeleting(false);
    }
  };

  const handleViewSignature = async (employee) => {
    setSignatureDialog({ open: true, employee, image: null, loading: true });
    try {
      const res = await axios.get(`${API}/admin/employee/${employee.id}/signature`);
      setSignatureDialog({
        open: true,
        employee,
        image: res.data?.signature_base64 || null,
        loading: false,
        created_at: res.data?.signature_created_at,
      });
    } catch (error) {
      toast.error("Erro ao carregar assinatura");
      setSignatureDialog({ open: false, employee: null, image: null, loading: false });
    }
  };

  const handleDeleteSignature = async () => {
    if (!deleteSignatureDialog.employee) return;
    setDeletingSignature(true);
    try {
      await axios.delete(`${API}/admin/employee/${deleteSignatureDialog.employee.id}/signature`);
      toast.success(`Assinatura de ${deleteSignatureDialog.employee.nome} removida`);
      setDeleteSignatureDialog({ open: false, employee: null });
      setSignatureDialog({ open: false, employee: null, image: null, loading: false });
      fetchData(currentPage, debouncedSearch);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao remover assinatura");
    } finally {
      setDeletingSignature(false);
    }
  };

  const handleViewPhoto = async (employee) => {
    setPhotoDialog({ open: true, employee, image: null, loading: true, created_at: null });
    try {
      const res = await axios.get(`${API}/admin/employee/${employee.id}/photo-3x4`);
      setPhotoDialog({
        open: true,
        employee,
        image: res.data?.photo_3x4_base64 || null,
        loading: false,
        created_at: res.data?.photo_3x4_created_at || null,
      });
    } catch (error) {
      // 404 = employee doesn't have a photo yet — open the dialog empty
      // so the admin can upload one. Any other error surfaces normally.
      if (error?.response?.status === 404) {
        setPhotoDialog({
          open: true, employee, image: null, loading: false, created_at: null,
        });
        return;
      }
      toast.error(error?.response?.data?.detail || "Erro ao carregar foto");
      setPhotoDialog({ open: false, employee: null, image: null, loading: false, created_at: null });
    }
  };

  const handleDownloadPhoto = () => {
    if (!photoDialog.image || !photoDialog.employee) return;
    const link = document.createElement("a");
    link.href = photoDialog.image;
    const safe = (photoDialog.employee.nome || "foto")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "_");
    link.download = `foto3x4_${photoDialog.employee.matricula || ""}_${safe}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const photoUploadInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const handleUploadPhotoFile = async (file) => {
    if (!file || !photoDialog.employee) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem (JPG/PNG).");
      return;
    }
    // Simple client-side size guard (10 MB max — server will re-crop).
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx. 10 MB).");
      return;
    }
    setUploadingPhoto(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Falha ao ler arquivo"));
        reader.readAsDataURL(file);
      });
      const res = await axios.post(
        `${API}/admin/employee/${photoDialog.employee.id}/photo-3x4`,
        { image_base64: dataUrl },
      );
      toast.success(`Foto de ${photoDialog.employee.nome} atualizada`);
      setPhotoDialog((prev) => ({
        ...prev,
        image: res.data?.photo_3x4_base64 || prev.image,
        created_at: res.data?.photo_3x4_created_at || prev.created_at,
      }));
      fetchData(currentPage, debouncedSearch);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao enviar foto");
    } finally {
      setUploadingPhoto(false);
      if (photoUploadInputRef.current) photoUploadInputRef.current.value = "";
    }
  };

  const handleDeletePhoto = async () => {
    if (!deletePhotoDialog.employee) return;
    setDeletingPhoto(true);
    try {
      await axios.delete(`${API}/admin/employee/${deletePhotoDialog.employee.id}/photo-3x4`);
      toast.success(`Foto de ${deletePhotoDialog.employee.nome} removida`);
      setDeletePhotoDialog({ open: false, employee: null });
      setPhotoDialog({ open: false, employee: null, image: null, loading: false, created_at: null });
      fetchData(currentPage, debouncedSearch);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao remover foto");
    } finally {
      setDeletingPhoto(false);
    }
  };

  const months = [
    { value: "1", label: "Janeiro" }, { value: "2", label: "Fevereiro" },
    { value: "3", label: "Março" }, { value: "4", label: "Abril" },
    { value: "5", label: "Maio" }, { value: "6", label: "Junho" },
    { value: "7", label: "Julho" }, { value: "8", label: "Agosto" },
    { value: "9", label: "Setembro" }, { value: "10", label: "Outubro" },
    { value: "11", label: "Novembro" }, { value: "12", label: "Dezembro" },
  ];

  const years = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year.toString(), label: year.toString() };
  });

  const maskCPF = (cpf) => {
    if (!cpf || cpf.length < 5) return "***.***.***-**";
    return `${cpf.slice(0, 3)}.***.***-${cpf.slice(-2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-safe">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 safe-area-top">
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-2">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                <img
                  src="/logo_full.png"
                  alt="AVI Segurança"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-lg font-semibold font-heading text-slate-900 truncate tracking-wide">
                  AVI SEGURANÇA
                </h1>
                <p className="text-[10px] sm:text-xs text-slate-500 truncate hidden sm:block">
                  Painel Administrativo
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <span className="text-xs sm:text-sm text-slate-600 hidden md:inline">
                Olá, <span className="font-medium">{user?.name}</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="h-9 px-2 sm:px-3"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Sair</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Menu de cards — tela inicial do admin (like the employee UI).
            Ao clicar num card, activeTab muda e a tela vai pra aquela área
            específica. O botão "Voltar ao menu" (renderizado logo abaixo)
            traz de volta pra este grid. */}
        {activeTab === "menu" && (
          <AdminMenuGrid onSelect={setActiveTab} />
        )}

        {activeTab !== "menu" && (
          <div className="mb-3 flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              onClick={() => setActiveTab("menu")}
              className="-ml-2 text-slate-700 hover:bg-slate-100"
              data-testid="admin-back-to-menu-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao menu
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTabsBarOpen((v) => !v)}
              className="text-slate-700"
              data-testid="admin-toggle-tabs-btn"
              title={tabsBarOpen ? "Ocultar abas" : "Mostrar todas as abas"}
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">
                {tabsBarOpen ? "Ocultar abas" : "Mostrar abas"}
              </span>
              {tabsBarOpen ? (
                <ChevronUp className="w-4 h-4 ml-1.5" />
              ) : (
                <ChevronDown className="w-4 h-4 ml-1.5" />
              )}
            </Button>
          </div>
        )}

        {/* Main Navigation Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => { setActiveTab(v); setTabsBarOpen(false); }}
          className="mb-6"
        >
          {activeTab !== "menu" && tabsBarOpen && (
            <TabsList
              className="w-full flex flex-wrap h-auto justify-start gap-1 p-1"
            >
            <TabsTrigger value="funcionarios" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-funcionarios">
              <UserCog className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Funcionários</span>
              <span className="xs:hidden sm:hidden">Func.</span>
            </TabsTrigger>
            <TabsTrigger value="contracheques" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-contracheques">
              <FileText className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Contracheques</span>
              <span className="xs:hidden sm:hidden">Folha</span>
            </TabsTrigger>
            <TabsTrigger value="central-comunicacao" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-central-comunicacao">
              <Radio className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Central de Comunicação</span>
              <span className="xs:hidden sm:hidden">Comunic.</span>
            </TabsTrigger>
            <TabsTrigger value="central-demissional" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-central-demissional">
              <UserMinus className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Central Demissional</span>
              <span className="xs:hidden sm:hidden">Demiss.</span>
            </TabsTrigger>
            <TabsTrigger value="informes" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink">
              <FileSpreadsheet className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Informe de Rendimentos</span>
              <span className="xs:hidden sm:hidden">Informe</span>
            </TabsTrigger>
            <TabsTrigger value="cursos" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-cursos">
              <GraduationCap className="w-4 h-4" />
              Cursos
            </TabsTrigger>
            <TabsTrigger value="folha-ponto" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-folha-ponto">
              <Clock className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Folha de Ponto</span>
              <span className="xs:hidden sm:hidden">Folha</span>
            </TabsTrigger>
            <TabsTrigger value="ferias" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-ferias">
              <Plane className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Recibos de Férias</span>
              <span className="xs:hidden sm:hidden">Recibos</span>
            </TabsTrigger>
            <TabsTrigger value="avisos-ferias" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-avisos-ferias">
              <CalendarClock className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Avisos de Férias</span>
              <span className="xs:hidden sm:hidden">Avisos</span>
            </TabsTrigger>
            <TabsTrigger value="kit-admissao" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-kit">
              <BadgeCheck className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Kit Admissão</span>
              <span className="xs:hidden sm:hidden">Admissão</span>
            </TabsTrigger>
            <TabsTrigger value="mensagens" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-mensagens">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Mensagens</span>
              <span className="xs:hidden sm:hidden">Msgs</span>
            </TabsTrigger>
            <TabsTrigger value="formularios" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-formularios">
              <ClipboardList className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Formulários</span>
              <span className="xs:hidden sm:hidden">Forms</span>
            </TabsTrigger>
            <TabsTrigger value="afd" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-afd">
              <Clock className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Converter AFD</span>
              <span className="xs:hidden sm:hidden">AFD</span>
            </TabsTrigger>
            <TabsTrigger value="supervisores" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-supervisores">
              <ShieldCheck className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Supervisores</span>
              <span className="xs:hidden sm:hidden">Sup.</span>
            </TabsTrigger>
            <TabsTrigger value="aso" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-aso">
              <Stethoscope className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Consulta de ASOs</span>
              <span className="xs:hidden sm:hidden">ASOs</span>
            </TabsTrigger>
            <TabsTrigger value="codigo-etica" className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 sm:flex-shrink" data-testid="admin-tab-codigo-etica">
              <BookOpen className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Código de Ética</span>
              <span className="xs:hidden sm:hidden">Ética</span>
            </TabsTrigger>
          </TabsList>
          )}

          {/* Contracheques Tab */}
          <TabsContent value="contracheques" className="mt-6">
            <div className="grid grid-cols-12 gap-6">
              {/* Left Column - Upload - Sticky */}
              <div className="col-span-12">
                <div className="lg:sticky lg:top-6 space-y-4">
                  <Card className="bg-white border-slate-200">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center font-heading text-base">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload de Contracheques
                      </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <AnimatePresence mode="wait">
                      {!previewData ? (
                        <motion.div
                          key="upload"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                      className="space-y-4"
                    >
                      <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        className={`dropzone border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer ${
                          dragActive ? "border-blue-500 bg-blue-50" 
                            : selectedFile ? "border-emerald-500 bg-emerald-50" 
                            : "border-slate-300 hover:border-slate-400"
                        }`}
                        onClick={() => document.getElementById("file-upload").click()}
                        data-testid="upload-dropzone"
                      >
                        <input
                          id="file-upload"
                          type="file"
                          accept=".pdf"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        
                        {selectedFile ? (
                          <div className="flex flex-col items-center">
                            <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-2" />
                            <p className="font-medium text-slate-900 text-sm">{selectedFile.name}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <FileUp className="w-10 h-10 text-slate-400 mb-2" />
                            <p className="font-medium text-slate-700 text-sm">
                              Arraste o PDF aqui
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              ou clique para selecionar
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Mês</Label>
                          <Select value={uploadMonth} onValueChange={setUploadMonth}>
                            <SelectTrigger className="h-9 text-sm">
                              <SelectValue placeholder="Auto" />
                            </SelectTrigger>
                            <SelectContent>
                              {months.map((month) => (
                                <SelectItem key={month.value} value={month.value}>
                                  {month.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Ano</Label>
                          <Select value={uploadYear} onValueChange={setUploadYear}>
                            <SelectTrigger className="h-9 text-sm">
                              <SelectValue placeholder="Auto" />
                            </SelectTrigger>
                            <SelectContent>
                              {years.map((year) => (
                                <SelectItem key={year.value} value={year.value}>
                                  {year.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Tipo do contracheque — permite subir mais de um PDF
                          por mês (ex.: normal + diferença salarial). */}
                      <div className="space-y-1">
                        <Label className="text-xs">Tipo do contracheque</Label>
                        <Select value={uploadTipo} onValueChange={setUploadTipo}>
                          <SelectTrigger
                            className="h-9 text-sm"
                            data-testid="upload-tipo-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Normal (folha do mês)</SelectItem>
                            <SelectItem value="diferenca">Diferença Salarial</SelectItem>
                            <SelectItem value="13o">13º Salário</SelectItem>
                            <SelectItem value="ferias">Férias</SelectItem>
                            <SelectItem value="complementar">Complementar</SelectItem>
                          </SelectContent>
                        </Select>
                        {uploadTipo !== "normal" && (
                          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                            Este PDF será salvo <b>além</b> do contracheque normal do mês (não substitui).
                          </p>
                        )}
                      </div>

                      {/* Progress Bar */}
                      {uploading && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600 flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                              {uploadPhase === "uploading" ? "Enviando..." : "Processando..."}
                            </span>
                            <span className="font-mono text-blue-600">{uploadProgress}%</span>
                          </div>
                          <Progress value={uploadProgress} className="h-3" />
                          <p className="text-xs text-slate-500 text-center">{progressMessage}</p>
                        </div>
                      )}

                      <Button
                        className="w-full bg-slate-900 hover:bg-slate-800"
                        onClick={handlePreview}
                        disabled={!selectedFile || uploading}
                        data-testid="preview-btn"
                      >
                        {uploading ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processando {uploadProgress}%</>
                        ) : (
                          <><Eye className="w-4 h-4 mr-2" />Visualizar</>
                        )}
                      </Button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="preview"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {previewData.employees.length} encontrados
                          </p>
                          {uploadTipo !== "normal" && (
                            <p className="text-[11px] mt-0.5 text-amber-700 font-semibold">
                              Tipo: {({
                                diferenca: "Diferença Salarial",
                                "13o": "13º Salário",
                                ferias: "Férias",
                                complementar: "Complementar",
                              })[uploadTipo]}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={handleCancelPreview}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="max-h-64 overflow-auto space-y-2">
                        {previewData.employees.map((emp) => (
                          <div
                            key={`${emp.matricula}-${emp.page_number}`}
                            className={`p-2 rounded border text-sm cursor-pointer transition-colors ${
                              selectedEmployees.includes(emp.matricula)
                                ? "bg-emerald-50 border-emerald-200"
                                : "bg-slate-50 border-slate-200"
                            }`}
                            onClick={() => toggleEmployeeSelection(emp.matricula)}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-mono text-blue-600">{emp.matricula}</span>
                                <span className="mx-2 text-slate-400">•</span>
                                <span className="text-slate-700">{emp.nome}</span>
                              </div>
                              {emp.is_new ? (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">Novo</Badge>
                              ) : emp.has_payslip ? (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">Substitui</Badge>
                              ) : (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">+</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {previewData.errors?.length > 0 && (
                        <div className="p-2 rounded bg-amber-50 border border-amber-200">
                          <p className="text-xs font-medium text-amber-700 flex items-center">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {previewData.errors.length} avisos
                          </p>
                        </div>
                      )}

                      {/* Save Progress Bar */}
                      {saving && uploadPhase === "saving" && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600 flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                              Salvando...
                            </span>
                            <span className="font-mono text-emerald-600">{uploadProgress}%</span>
                          </div>
                          <Progress value={uploadProgress} className="h-3" />
                          <p className="text-xs text-slate-500 text-center">{progressMessage}</p>
                        </div>
                      )}

                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={handleSaveEmployees}
                        disabled={saving || selectedEmployees.length === 0}
                        data-testid="save-employees-btn"
                      >
                        {saving ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando {uploadProgress}%</>
                        ) : (
                          <><Save className="w-4 h-4 mr-2" />Salvar {selectedEmployees.length}</>
                        )}
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>

            {/* Periods Management Card */}
            <Card className="bg-white border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center font-heading text-base">
                  <Calendar className="w-4 h-4 mr-2" />
                  Períodos Cadastrados
                </CardTitle>
                <CardDescription className="text-xs">
                  Gerencie os contracheques por mês/ano
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingPeriods ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : periods.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Nenhum período cadastrado
                  </p>
                ) : (
                  <div className="space-y-2">
                    {periods.map((period) => (
                      <div 
                        key={`${period.ano}-${period.mes}`}
                        className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <FileText className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {period.mes_nome} / {period.ano}
                            </p>
                            <p className="text-xs text-slate-500">
                              {period.quantidade} contracheque{period.quantidade !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          onClick={() => setDeletePeriodDialog({ open: true, period })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Storage Info Card */}
            <Card className="bg-white border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center font-heading text-base">
                  <Database className="w-4 h-4 mr-2" />
                  Armazenamento
                </CardTitle>
                <CardDescription className="text-xs">
                  Uso do banco de dados MongoDB Atlas
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingStorage ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Storage Bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-600">
                          {storageInfo.used_mb} MB usados
                        </span>
                        <span className="text-slate-500">
                          {storageInfo.total_mb} MB total
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            storageInfo.used_percent > 80 
                              ? 'bg-red-500' 
                              : storageInfo.used_percent > 60 
                                ? 'bg-yellow-500' 
                                : 'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(storageInfo.used_percent, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className={`font-medium ${
                          storageInfo.used_percent > 80 
                            ? 'text-red-600' 
                            : storageInfo.used_percent > 60 
                              ? 'text-yellow-600' 
                              : 'text-emerald-600'
                        }`}>
                          {storageInfo.used_percent}% usado
                        </span>
                        <span className="text-slate-500">
                          {storageInfo.remaining_mb} MB disponível
                        </span>
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                      <div className="text-center p-2 bg-slate-50 rounded">
                        <p className="text-lg font-semibold text-slate-900">{storageInfo.employees_count || 0}</p>
                        <p className="text-xs text-slate-500">Funcionários</p>
                      </div>
                      <div className="text-center p-2 bg-slate-50 rounded">
                        <p className="text-lg font-semibold text-slate-900">{storageInfo.payslips_count || 0}</p>
                        <p className="text-xs text-slate-500">Contracheques</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Access Statistics Card */}
            <Card className="bg-white border-slate-200 mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center font-heading text-base">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Estatísticas de Acesso
                </CardTitle>
                <CardDescription className="text-xs">
                  Acessos de funcionários ao sistema
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Month/Year Selector */}
                <div className="flex gap-2 mb-4">
                  <select
                    value={accessStatsMonth}
                    onChange={(e) => setAccessStatsMonth(Number(e.target.value))}
                    className="flex-1 h-8 text-xs border border-slate-200 rounded px-2 bg-white"
                  >
                    <option value={1}>Janeiro</option>
                    <option value={2}>Fevereiro</option>
                    <option value={3}>Março</option>
                    <option value={4}>Abril</option>
                    <option value={5}>Maio</option>
                    <option value={6}>Junho</option>
                    <option value={7}>Julho</option>
                    <option value={8}>Agosto</option>
                    <option value={9}>Setembro</option>
                    <option value={10}>Outubro</option>
                    <option value={11}>Novembro</option>
                    <option value={12}>Dezembro</option>
                  </select>
                  <select
                    value={accessStatsYear}
                    onChange={(e) => setAccessStatsYear(Number(e.target.value))}
                    className="w-20 h-8 text-xs border border-slate-200 rounded px-2 bg-white"
                  >
                    {[2024, 2025, 2026, 2027].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                {/* Download Monthly Report PDF */}
                <button
                  type="button"
                  data-testid="download-monthly-report-btn"
                  onClick={async () => {
                    try {
                      const url = `${API}/admin/reports/monthly.pdf?year=${accessStatsYear}&month=${accessStatsMonth}`;
                      const res = await axios.get(url, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: "blob",
                      });
                      const blob = new Blob([res.data], { type: "application/pdf" });
                      const href = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = href;
                      a.download = `avi_relatorio_${accessStatsYear}-${String(accessStatsMonth).padStart(2, "0")}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => URL.revokeObjectURL(href), 1000);
                    } catch (e) {
                      toast.error("Falha ao gerar relatório PDF");
                    }
                  }}
                  className="w-full mb-4 h-9 rounded-md bg-sky-700 hover:bg-sky-800 text-white text-xs font-semibold flex items-center justify-center gap-2 transition"
                >
                  <FileText className="w-4 h-4" />
                  Baixar Relatório Mensal (PDF)
                </button>

                {loadingAccessStats ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <p className="text-xl font-bold text-blue-600">{accessStats.total_accesses}</p>
                        <p className="text-xs text-blue-600/70">Total Acessos</p>
                      </div>
                      <div className="text-center p-3 bg-emerald-50 rounded-lg">
                        <p className="text-xl font-bold text-emerald-600">{accessStats.unique_employees}</p>
                        <p className="text-xs text-emerald-600/70">Únicos</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <p className="text-xl font-bold text-purple-600">{accessStats.avg_per_day}</p>
                        <p className="text-xs text-purple-600/70">Média/dia</p>
                      </div>
                    </div>

                    {/* Signature Progress */}
                    {accessStats.signatures_total > 0 && (
                      <div
                        className="p-3 bg-amber-50 rounded-lg border border-amber-100"
                        data-testid="signatures-progress-card"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-amber-700 flex items-center">
                            <PenLine className="w-3 h-3 mr-1" />
                            Assinaturas Cadastradas
                          </p>
                          <p className="text-xs font-mono text-amber-700">
                            {accessStats.signatures_pct}%
                          </p>
                        </div>
                        <div className="flex items-baseline gap-2 mb-2">
                          <span
                            className="text-xl font-bold text-amber-700"
                            data-testid="signatures-signed-count"
                          >
                            {accessStats.signatures_signed}
                          </span>
                          <span className="text-xs text-amber-700/70">
                            de {accessStats.signatures_total} funcionários
                          </span>
                          <span className="ml-auto text-xs text-amber-700/70">
                            {accessStats.signatures_pending} pendentes
                          </span>
                        </div>
                        <div className="w-full bg-amber-100 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="bg-amber-500 h-1.5 rounded-full transition-all"
                            style={{ width: `${accessStats.signatures_pct}%` }}
                          />
                        </div>
                        {accessStats.signatures_pending > 0 && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const r = await axios.get(
                                  `${API}/admin/signatures/pending/export`,
                                  {
                                    headers: { Authorization: `Bearer ${token}` },
                                    responseType: "blob",
                                    timeout: 60000,
                                  },
                                );
                                const url = URL.createObjectURL(r.data);
                                const a = document.createElement("a");
                                const stamp = new Date().toISOString().slice(0, 10);
                                a.href = url;
                                a.download = `AVI-Rubricas-Pendentes-${stamp}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                                toast.success(
                                  `${r.headers["x-pending-count"] || ""} funcionário(s) exportado(s).`,
                                );
                              } catch (e) {
                                toast.error("Falha ao gerar PDF de pendências.");
                              }
                            }}
                            className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-md px-2 py-1 transition-colors"
                            data-testid="signatures-export-pending-btn"
                            title="Baixa a lista de funcionários sem rúbrica em PDF (nome, matrícula, posto, telefone)"
                          >
                            <Download className="w-3 h-3" />
                            Exportar {accessStats.signatures_pending} pendente(s) em PDF
                          </button>
                        )}
                      </div>
                    )}

                    {/* Top Employees */}
                    {accessStats.top_employees && accessStats.top_employees.length > 0 && (
                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs font-medium text-slate-600 mb-2 flex items-center">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Top Funcionários
                        </p>
                        <div className="space-y-1">
                          {accessStats.top_employees.slice(0, 5).map((emp, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs py-1">
                              <span className="text-slate-600 truncate flex-1">
                                {emp.name}
                              </span>
                              <span className="font-mono text-slate-400 ml-2">
                                {emp.count}x
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {accessStats.total_accesses === 0 && (
                      <p className="text-xs text-slate-400 text-center py-2">
                        Nenhum acesso registrado neste período
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </div>
          </div>
          </TabsContent>

          {/* Funcionários Tab (Gestão de cadastro) */}
          <TabsContent value="funcionarios" className="mt-6">
            {/* Segmented control: Funcionários / Postos / Contratos */}
            <div className="mb-4 flex justify-center sm:justify-start">
              <div
                className="inline-flex items-center gap-1 p-1 bg-white border border-slate-200 rounded-full shadow-sm"
                data-testid="funcionarios-view-toggle"
              >
                <button
                  type="button"
                  onClick={() => setFuncSubView("employees")}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    funcSubView === "employees"
                      ? "bg-slate-900 text-white shadow"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                  data-testid="funcionarios-view-employees"
                >
                  <UserCog className="w-3.5 h-3.5" />
                  Funcionários
                </button>
                <button
                  type="button"
                  onClick={() => setFuncSubView("postos")}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    funcSubView === "postos"
                      ? "bg-slate-900 text-white shadow"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                  data-testid="funcionarios-view-postos"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  Postos
                </button>
                <button
                  type="button"
                  onClick={() => setFuncSubView("contratos")}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    funcSubView === "contratos"
                      ? "bg-slate-900 text-white shadow"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                  data-testid="funcionarios-view-contratos"
                >
                  <FileCheck className="w-3.5 h-3.5" />
                  Contratos
                </button>
              </div>
            </div>

            {funcSubView === "postos" ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="pt-6">
                  <AdminPostosView onBack={() => setFuncSubView("employees")} />
                </CardContent>
              </Card>
            ) : funcSubView === "contratos" ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="pt-6">
                  <AdminContratosView onBack={() => setFuncSubView("employees")} />
                </CardContent>
              </Card>
            ) : (
            <Card className="bg-white border-slate-200">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="flex items-center font-heading text-base">
                    <UserCog className="w-4 h-4 mr-2" />
                    Gestão de Funcionários ({totalEmployees})
                  </CardTitle>
                  <div className="flex items-center flex-wrap gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => setCreateEmployeeOpen(true)}
                      className="h-8 text-xs bg-blue-700 hover:bg-blue-800 text-white"
                      data-testid="btn-create-employee"
                    >
                      <Users className="w-3 h-3 mr-1" />
                      Novo funcionário
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setImportRosterOpen(true)}
                      className="h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid="btn-import-roster"
                    >
                      <FileSpreadsheet className="w-3 h-3 mr-1" />
                      Importar Cadastro
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBulkDeleteOpen(true)}
                      className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50"
                      data-testid="btn-bulk-delete"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Lista de Exclusão
                    </Button>
                    {totalEmployees > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50 border-red-200 h-8 text-xs"
                        onClick={() => setDeleteAllDialog(true)}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Apagar Todos
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => fetchData(currentPage, debouncedSearch)} className="h-8 text-xs">
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Atualizar
                    </Button>
                  </div>
                </div>

                {(employees.length > 0 || searchTerm) && (
                  <div className="mt-3 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <Input
                      placeholder="Buscar por matrícula ou nome..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 pr-16 h-9"
                      data-testid="search-input-funcionarios"
                    />
                    {loading && (
                      <Loader2
                        className="absolute right-9 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 animate-spin"
                        aria-label="Buscando"
                      />
                    )}
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                        title="Limpar busca"
                        data-testid="search-clear-funcionarios"
                        aria-label="Limpar busca"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                {employees.length === 0 && !searchTerm ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Nenhum funcionário cadastrado</p>
                    <p className="text-xs text-slate-400 mt-2">
                      Cadastre um novo funcionário ou importe uma planilha para começar.
                    </p>
                  </div>
                ) : filteredEmployees.length === 0 ? (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Nenhum resultado encontrado</p>
                    <p className="text-xs text-slate-400 mt-2">
                      Verifique a matrícula ou o nome digitado.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSearchTerm("")}
                      className="mt-4 h-8 text-xs"
                      data-testid="search-clear-empty-state"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Limpar busca
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-slate-50 z-10">
                          <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold text-xs w-12 py-2"></TableHead>
                            <TableHead className="font-semibold text-xs">Matrícula</TableHead>
                            <TableHead
                              className="font-semibold text-xs cursor-pointer hover:bg-slate-100 select-none"
                              onClick={() => handleSort("nome")}
                            >
                              <div className="flex items-center">
                                Nome
                                {getSortIcon("nome")}
                              </div>
                            </TableHead>
                            <TableHead className="font-semibold text-xs">CPF</TableHead>
                            <TableHead
                              className="font-semibold text-xs text-center w-24 cursor-pointer hover:bg-slate-100 select-none"
                              onClick={() => handleSort("access_count")}
                            >
                              <div className="flex items-center justify-center">
                                Acessos
                                {getSortIcon("access_count")}
                              </div>
                            </TableHead>
                            <TableHead className="font-semibold text-xs text-center w-24">
                              Assinatura
                            </TableHead>
                            <TableHead className="font-semibold text-xs text-center w-24">
                              Foto 3x4
                            </TableHead>
                            <TableHead className="font-semibold text-xs w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredEmployees.map((employee) => (
                            <TableRow key={employee.id} className="hover:bg-slate-50">
                              <TableCell className="py-1.5 pl-3 pr-1 w-12">
                                {employee.photo_3x4_base64 ? (
                                  <button
                                    type="button"
                                    onClick={() => handleViewPhoto(employee)}
                                    className="block group focus:outline-none focus:ring-2 focus:ring-sky-400 rounded"
                                    title="Ver foto 3x4"
                                    data-testid={`thumb-photo-${employee.matricula}`}
                                  >
                                    <img
                                      src={employee.photo_3x4_base64}
                                      alt={employee.nome}
                                      className="w-9 h-12 object-cover rounded border border-slate-200 shadow-sm bg-white group-hover:border-sky-400 transition-colors"
                                    />
                                  </button>
                                ) : (
                                  <div
                                    className="w-9 h-12 rounded border border-slate-200 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-500 uppercase"
                                    title="Sem foto"
                                  >
                                    {(employee.nome || "?")
                                      .split(" ")
                                      .filter(Boolean)
                                      .slice(0, 2)
                                      .map((s) => s[0] || "")
                                      .join("")}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="font-mono text-blue-600 text-sm py-2">
                                {employee.matricula}
                              </TableCell>
                              <TableCell className="text-sm py-2">{employee.nome}</TableCell>
                              <TableCell className="font-mono text-slate-500 text-sm py-2">
                                {employee.cpf_masked}
                              </TableCell>
                              <TableCell className="text-center py-2">
                                {employee.access_count > 0 ? (
                                  <span className="inline-flex items-center justify-center min-w-[24px] px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                    {employee.access_count}
                                  </span>
                                ) : (
                                  <span className="text-slate-300 text-xs">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center py-2">
                                {employee.has_signature ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                                    onClick={() => handleViewSignature(employee)}
                                    data-testid={`view-signature-${employee.matricula}`}
                                    title="Ver assinatura"
                                  >
                                    <PenLine className="w-3.5 h-3.5 mr-1" />
                                    <span className="text-xs">Ver</span>
                                  </Button>
                                ) : (
                                  <span className="text-slate-300 text-xs">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center py-2">
                                {employee.has_photo_3x4 ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-sky-700 hover:text-sky-800 hover:bg-sky-50"
                                    onClick={() => handleViewPhoto(employee)}
                                    data-testid={`view-photo-${employee.matricula}`}
                                    title="Ver foto 3x4"
                                  >
                                    <UserSquare2 className="w-3.5 h-3.5 mr-1" />
                                    <span className="text-xs">Ver</span>
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                                    onClick={() => handleViewPhoto(employee)}
                                    data-testid={`upload-photo-${employee.matricula}`}
                                    title="Enviar foto 3x4"
                                  >
                                    <Upload className="w-3.5 h-3.5 mr-1" />
                                    <span className="text-xs">Enviar</span>
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell className="py-2">
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-600 hover:bg-blue-50 h-7 w-7 p-0"
                                    onClick={() => setEditEmployeeId(employee.id)}
                                    title="Editar dados"
                                    data-testid={`edit-employee-${employee.matricula}`}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                                    onClick={() => setDeleteDialog({ open: true, employee })}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="text-sm text-slate-500">
                      Mostrando {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, totalEmployees)} de {totalEmployees}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1 || loading}
                        className="h-8 px-2"
                      >
                        «
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1 || loading}
                        className="h-8 px-3"
                      >
                        Anterior
                      </Button>
                      <span className="text-sm px-3 py-1 bg-slate-100 rounded">
                        {currentPage} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages || loading}
                        className="h-8 px-3"
                      >
                        Próximo
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages || loading}
                        className="h-8 px-2"
                      >
                        »
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* Central de Comunicação — Quadro de Avisos + Disparo WhatsApp + Saúde Z-API */}
          <TabsContent value="central-comunicacao" className="mt-6">
            <div className="space-y-4">
              {/* Banner do Portal — fullscreen na abertura */}
              <AdminBannersPanel />

              {/* Vídeo de Comunicação — fullscreen bloqueante no login */}
              <AdminVideosPanel />

              {/* Announcement Card */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center font-heading text-base">
                    <Megaphone className="w-4 h-4 mr-2" />
                    Quadro de Avisos
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Mensagem exibida para todos os funcionários
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <textarea
                      value={announcementText}
                      onChange={(e) => setAnnouncementText(e.target.value)}
                      placeholder="Digite o aviso para os funcionários..."
                      className="w-full h-24 p-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={400}
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={announcementActive}
                          onChange={(e) => setAnnouncementActive(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-xs text-slate-600">Ativar aviso</span>
                      </label>
                      <span className="text-xs text-slate-400">
                        {announcementText.length}/400
                      </span>
                    </div>
                    <Button
                      onClick={handleSaveAnnouncement}
                      disabled={savingAnnouncement}
                      className="w-full h-8 text-xs"
                      size="sm"
                    >
                      {savingAnnouncement ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Save className="w-3 h-3 mr-1" />
                      )}
                      Salvar Aviso
                    </Button>
                    {announcement.active && announcement.text && (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-800 font-medium">Preview:</p>
                        <p className="text-xs text-amber-700 mt-1">{announcement.text}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* WhatsApp Broadcast Card */}
              <AdminWhatsappBroadcast />

              {/* Z-API Health Card */}
              <div data-testid="zapi-health-section">
                <AdminZapiHealth />
              </div>
            </div>
          </TabsContent>

          {/* Central Demissional — Término de Contrato */}
          <TabsContent value="central-demissional" className="mt-6">
            <AdminCentralDemissionalTab />
          </TabsContent>

          {/* Informe de Rendimentos Tab */}
          <TabsContent value="informes" className="mt-6">
            <div className="grid grid-cols-12 gap-6">
              {/* Left Column - Upload */}
              <div className="col-span-12 lg:col-span-5">
                <div className="space-y-4">
                  <Card className="bg-white border-slate-200">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center font-heading text-base">
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Upload de Informe de Rendimentos
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Faça upload do PDF com todos os informes
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {!incomeReportPreview ? (
                        <div className="space-y-4">
                          <div 
                            className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-400 transition-colors cursor-pointer"
                            onClick={() => document.getElementById('income-report-input').click()}
                          >
                            <FileUp className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                            <p className="text-sm text-slate-600 mb-1">
                              {incomeReportFile ? incomeReportFile.name : "Clique para selecionar"}
                            </p>
                            <p className="text-xs text-slate-400">PDF com todos os informes</p>
                            <input
                              type="file"
                              id="income-report-input"
                              accept=".pdf"
                              onChange={(e) => setIncomeReportFile(e.target.files[0])}
                              className="hidden"
                            />
                          </div>
                          
                          {incomeReportFile && (
                            <Button
                              onClick={handleIncomeReportUpload}
                              disabled={incomeReportUploading}
                              className="w-full"
                            >
                              {incomeReportUploading ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Processando PDF...
                                </>
                              ) : (
                                <>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Visualizar Preview
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Preview Stats */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-blue-50 p-3 rounded-lg text-center">
                              <p className="text-2xl font-bold text-blue-600">{incomeReportPreview.total_pages}</p>
                              <p className="text-xs text-blue-600/70">Páginas</p>
                            </div>
                            <div className="bg-emerald-50 p-3 rounded-lg text-center">
                              <p className="text-2xl font-bold text-emerald-600">{incomeReportPreview.employees_found}</p>
                              <p className="text-xs text-emerald-600/70">Funcionários</p>
                            </div>
                          </div>
                          
                          {/* Selection summary */}
                          <div className="bg-slate-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-slate-700">
                              {selectedIncomeReports.length} selecionados para salvar
                            </p>
                            <p className="text-xs text-slate-500">
                              {incomeReportPreview.employees.filter(e => !e.employee_exists).length} não encontrados no sistema
                            </p>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                setIncomeReportPreview(null);
                                setIncomeReportFile(null);
                                setSelectedIncomeReports([]);
                              }}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              className="flex-1"
                              onClick={handleSaveIncomeReports}
                              disabled={incomeReportSaving || selectedIncomeReports.length === 0}
                            >
                              {incomeReportSaving ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Save className="w-4 h-4 mr-1" />
                              )}
                              Salvar ({selectedIncomeReports.length})
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Saved Income Reports */}
                  <Card className="bg-white border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center font-heading text-base">
                        <Calendar className="w-4 h-4 mr-2" />
                        Informes Cadastrados
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {incomeReportStats.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">
                          Nenhum informe cadastrado
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {incomeReportStats.map((stat) => (
                            <div
                              key={stat.ano}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                            >
                              <div>
                                <p className="font-medium text-slate-900">{stat.ano}</p>
                                <p className="text-xs text-slate-500">{stat.quantidade} informes</p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteIncomeReportYear(stat.ano)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Right Column - Preview List */}
              <div className="col-span-12 lg:col-span-7">
                <Card className="bg-white border-slate-200">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center font-heading text-base">
                      <Users className="w-4 h-4 mr-2" />
                      {incomeReportPreview ? "Preview dos Informes" : "Funcionários com Informe"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {incomeReportPreview ? (
                      <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                        <div className="space-y-2">
                          {incomeReportPreview.employees.map((emp, idx) => (
                            <div
                              key={idx}
                              className={`flex items-center justify-between p-3 rounded-lg border ${
                                emp.employee_exists 
                                  ? 'bg-white border-slate-200 hover:border-slate-300' 
                                  : 'bg-red-50 border-red-200'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={selectedIncomeReports.includes(emp.cpf_clean)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedIncomeReports([...selectedIncomeReports, emp.cpf_clean]);
                                    } else {
                                      setSelectedIncomeReports(selectedIncomeReports.filter(c => c !== emp.cpf_clean));
                                    }
                                  }}
                                  disabled={!emp.employee_exists}
                                />
                                <div>
                                  <p className="text-sm font-medium text-slate-900">{emp.nome}</p>
                                  <p className="text-xs text-slate-500">
                                    CPF: {emp.cpf} | Ano: {emp.ano} | {emp.page_count} pág.
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {emp.employee_exists ? (
                                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                                    {emp.matricula}
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="bg-red-100 text-red-700">
                                    Não cadastrado
                                  </Badge>
                                )}
                                {emp.has_report && (
                                  <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                                    Já existe
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-8">
                        Faça upload de um PDF para visualizar os informes
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Cursos Tab */}
          <TabsContent value="cursos" className="mt-6">
            <AdminCoursesTab />
          </TabsContent>

          {/* Folha de Ponto Tab */}
          <TabsContent value="folha-ponto" className="mt-6">
            <AdminFolhaPontoTab />
          </TabsContent>

          {/* Recibos de Férias Tab */}
          <TabsContent value="ferias" className="mt-6">
            <AdminVacationNoticesTab />
          </TabsContent>

          {/* Avisos de Férias Tab */}
          <TabsContent value="avisos-ferias" className="mt-6">
            <AdminVacationAdvancesTab />
          </TabsContent>

          {/* Kit de Admissão Tab (now hosts Contratos / EPI / Crachá / VT
              as sub-tabs, with the kit upload button at the top). */}
          <TabsContent value="kit-admissao" className="mt-6">
            <AdminAdmissionKitsTab adminToken={token} />
          </TabsContent>

          {/* Quadro de Mensagens Tab */}
          <TabsContent value="mensagens" className="mt-6">
            <AdminMessageTemplatesTab />
          </TabsContent>

          {/* Formulários Públicos Tab */}
          <TabsContent value="formularios" className="mt-6">
            <AdminFormsTab token={token} />
          </TabsContent>

          {/* Converter AFD Tab */}
          <TabsContent value="afd" className="mt-6">
            <AdminAfdTab token={token} />
          </TabsContent>

          {/* Supervisores do Painel de Operações */}
          <TabsContent value="supervisores" className="mt-6">
            <AdminOpsSupervisorsTab />
          </TabsContent>

          {/* Consulta de ASOs — upload de planilha */}
          <TabsContent value="aso" className="mt-6">
            <AdminAsoUploadTab />
          </TabsContent>

          {/* Código de Ética e Conduta — biblioteca de PDFs */}
          <TabsContent value="codigo-etica" className="mt-6">
            <AdminCodigoEticaTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, employee: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Remover <strong>{deleteDialog.employee?.nome}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, employee: null })}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteEmployee} disabled={deleting}>
              {deleting ? "Excluindo..." : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Dialog */}
      <Dialog open={deleteAllDialog} onOpenChange={setDeleteAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Apagar Todos</DialogTitle>
            <DialogDescription>
              Remover <strong>{totalEmployees}</strong> funcionários e todos os contracheques?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAllDialog(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteAllEmployees} disabled={deleting}>
              {deleting ? "Excluindo..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportRosterDialog
        open={importRosterOpen}
        onOpenChange={setImportRosterOpen}
        onImported={() => fetchData(currentPage, debouncedSearch)}
      />

      <EditEmployeeDialog
        open={!!editEmployeeId}
        employeeId={editEmployeeId}
        onClose={() => setEditEmployeeId(null)}
        onSaved={() => fetchData(currentPage, debouncedSearch)}
      />

      <EditEmployeeDialog
        mode="create"
        open={createEmployeeOpen}
        onClose={() => setCreateEmployeeOpen(false)}
        onSaved={() => fetchData(currentPage, debouncedSearch)}
      />

      <BulkDeleteEmployeesDialog
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onDeleted={() => fetchData(currentPage, debouncedSearch)}
      />

      {/* Delete Period Dialog */}
      <Dialog open={deletePeriodDialog.open} onOpenChange={(open) => setDeletePeriodDialog({ open, period: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Excluir Período</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir todos os <strong>{deletePeriodDialog.period?.quantidade}</strong> contracheques de <strong>{deletePeriodDialog.period?.mes_nome}/{deletePeriodDialog.period?.ano}</strong>?
              <br /><br />
              <span className="text-amber-600">⚠️ Esta ação não pode ser desfeita.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePeriodDialog({ open: false, period: null })}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeletePeriod} disabled={deletingPeriod}>
              {deletingPeriod ? "Excluindo..." : "Excluir Período"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Signature Dialog */}
      <Dialog
        open={signatureDialog.open}
        onOpenChange={(open) =>
          setSignatureDialog({ open, employee: null, image: null, loading: false })
        }
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-4 h-4 text-emerald-700" />
              Assinatura de {signatureDialog.employee?.nome}
            </DialogTitle>
            <DialogDescription>
              Matrícula: <span className="font-mono">{signatureDialog.employee?.matricula}</span>
              {signatureDialog.created_at && (
                <span className="block text-xs mt-1">
                  Cadastrada em {new Date(signatureDialog.created_at).toLocaleString("pt-BR")}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {signatureDialog.loading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : signatureDialog.image ? (
              <div className="rounded-md border border-slate-200 bg-white p-4 flex items-center justify-center">
                <img
                  src={signatureDialog.image}
                  alt="Assinatura"
                  className="max-h-48 object-contain"
                  data-testid="admin-signature-image"
                />
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">
                Nenhuma assinatura cadastrada.
              </p>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() =>
                setSignatureDialog({ open: false, employee: null, image: null, loading: false })
              }
            >
              Fechar
            </Button>
            {signatureDialog.image && (
              <Button
                variant="destructive"
                onClick={() =>
                  setDeleteSignatureDialog({ open: true, employee: signatureDialog.employee })
                }
                data-testid="delete-signature-btn"
              >
                <ShieldOff className="w-4 h-4 mr-2" />
                Resetar assinatura
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete signature */}
      <Dialog
        open={deleteSignatureDialog.open}
        onOpenChange={(open) =>
          setDeleteSignatureDialog({ open, employee: deleteSignatureDialog.employee })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Resetar Assinatura</DialogTitle>
            <DialogDescription>
              Remover a assinatura de <strong>{deleteSignatureDialog.employee?.nome}</strong>?
              <br />
              No próximo login, o funcionário será obrigado a cadastrar uma nova rúbrica.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteSignatureDialog({ open: false, employee: null })}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSignature}
              disabled={deletingSignature}
              data-testid="confirm-delete-signature-btn"
            >
              {deletingSignature ? "Removendo..." : "Resetar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View 3x4 Badge Photo Dialog */}
      <Dialog
        open={photoDialog.open}
        onOpenChange={(open) =>
          setPhotoDialog({ open, employee: null, image: null, loading: false, created_at: null })
        }
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserSquare2 className="w-4 h-4 text-sky-700" />
              Foto 3x4 de {photoDialog.employee?.nome}
            </DialogTitle>
            <DialogDescription>
              Matrícula: <span className="font-mono">{photoDialog.employee?.matricula}</span>
              {photoDialog.created_at && (
                <span className="block text-xs mt-1">
                  Capturada em {new Date(photoDialog.created_at).toLocaleString("pt-BR")}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {photoDialog.loading ? (
              <div className="flex items-center justify-center h-56">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : photoDialog.image ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 flex items-center justify-center">
                <img
                  src={photoDialog.image}
                  alt="Foto 3x4"
                  className="w-[240px] h-[320px] object-cover rounded shadow-md"
                  data-testid="admin-photo-3x4-image"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                <ImageOff className="w-10 h-10 mb-2" />
                <p className="text-sm">Nenhuma foto cadastrada.</p>
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-wrap gap-2 justify-end sm:space-x-0">
            <input
              ref={photoUploadInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleUploadPhotoFile(e.target.files?.[0])}
              data-testid="admin-photo-3x4-upload-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setPhotoDialog({ open: false, employee: null, image: null, loading: false, created_at: null })
              }
            >
              Fechar
            </Button>
            {photoDialog.employee && (
              <Button
                variant="outline"
                size="sm"
                disabled={uploadingPhoto}
                onClick={() => photoUploadInputRef.current?.click()}
                data-testid="admin-upload-photo-3x4-btn"
                className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              >
                {uploadingPhoto ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando…
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {photoDialog.image ? "Substituir foto" : "Enviar foto"}
                  </>
                )}
              </Button>
            )}
            {photoDialog.employee && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  window.open(
                    `${API}/admin/employee/${photoDialog.employee.id}/badge.pdf?v=${Date.now()}`,
                    "_blank",
                  )
                }
                data-testid="admin-download-badge-btn"
                className="text-slate-900 border-slate-300 hover:bg-slate-50"
              >
                <IdCard className="w-4 h-4 mr-2" />
                Baixar Crachá
              </Button>
            )}
            {photoDialog.image && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPhoto}
                  data-testid="download-photo-3x4-btn"
                  className="text-sky-700 border-sky-200 hover:bg-sky-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() =>
                    setDeletePhotoDialog({ open: true, employee: photoDialog.employee })
                  }
                  data-testid="delete-photo-3x4-btn"
                >
                  <ImageOff className="w-4 h-4 mr-2" />
                  Resetar foto
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete 3x4 photo */}
      <Dialog
        open={deletePhotoDialog.open}
        onOpenChange={(open) =>
          setDeletePhotoDialog({ open, employee: deletePhotoDialog.employee })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Resetar Foto 3x4</DialogTitle>
            <DialogDescription>
              Remover a foto de <strong>{deletePhotoDialog.employee?.nome}</strong>?
              <br />
              No próximo login, o funcionário será obrigado a capturar uma nova foto.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeletePhotoDialog({ open: false, employee: null })}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletePhoto}
              disabled={deletingPhoto}
              data-testid="confirm-delete-photo-3x4-btn"
            >
              {deletingPhoto ? "Removendo..." : "Resetar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Home menu grid ----------
// Renderizado quando activeTab === "menu". Um card por área do painel
// admin, com gradientes cheios pra dar identidade visual (mesmo padrão
// do EmployeeDashboard). Ao clicar, define activeTab pra abrir a área.
const ADMIN_MENU_ITEMS = [
  {
    id: "funcionarios", title: "Gestão de Funcionários",
    subtitle: "Cadastro, edição, exclusão e importação",
    icon: UserCog, gradient: "from-indigo-600 to-violet-700",
    testId: "admin-menu-funcionarios",
  },
  {
    id: "contracheques", title: "Contracheques",
    subtitle: "Upload, cadastro e distribuição da folha",
    icon: FileText, gradient: "from-blue-600 to-indigo-700",
    testId: "admin-menu-contracheques",
  },
  {
    id: "central-comunicacao", title: "Central de Comunicação",
    subtitle: "Novo módulo — em construção",
    icon: Radio, gradient: "from-fuchsia-600 to-pink-700",
    testId: "admin-menu-central-comunicacao",
  },
  {
    id: "central-demissional", title: "Central Demissional",
    subtitle: "Novo módulo — em construção",
    icon: UserMinus, gradient: "from-orange-600 to-red-700",
    testId: "admin-menu-central-demissional",
  },
  {
    id: "informes", title: "Informe de Rendimentos",
    subtitle: "Distribua o informe anual de IR",
    icon: FileSpreadsheet, gradient: "from-emerald-600 to-teal-700",
    testId: "admin-menu-informes",
  },
  {
    id: "cursos", title: "Cursos EAD",
    subtitle: "Convocações, listas e certificados NR",
    icon: GraduationCap, gradient: "from-amber-500 to-orange-600",
    testId: "admin-menu-cursos",
  },
  {
    id: "folha-ponto", title: "Folha de Ponto",
    subtitle: "Convoque o envio da foto mensal",
    icon: Clock, gradient: "from-cyan-700 to-teal-800",
    testId: "admin-menu-folha-ponto",
  },
  {
    id: "ferias", title: "Recibos de Férias",
    subtitle: "Envie recibos com assinatura digital",
    icon: Plane, gradient: "from-sky-600 to-cyan-700",
    testId: "admin-menu-ferias",
  },
  {
    id: "avisos-ferias", title: "Avisos de Férias",
    subtitle: "Comunique o período de férias",
    icon: CalendarClock, gradient: "from-cyan-600 to-blue-700",
    testId: "admin-menu-avisos-ferias",
  },
  {
    id: "kit-admissao", title: "Kit de Admissão",
    subtitle: "Contrato, EPI, crachá, VT e mais",
    icon: BadgeCheck, gradient: "from-violet-600 to-purple-700",
    testId: "admin-menu-kit",
  },
  {
    id: "mensagens", title: "Mensagens",
    subtitle: "Templates de comunicação em massa",
    icon: MessageSquare, gradient: "from-pink-600 to-rose-700",
    testId: "admin-menu-mensagens",
  },
  {
    id: "formularios", title: "Formulários",
    subtitle: "Colete respostas externas por link",
    icon: ClipboardList, gradient: "from-fuchsia-600 to-pink-700",
    testId: "admin-menu-formularios",
  },
  {
    id: "afd", title: "Converter AFD",
    subtitle: "Ponto eletrônico → PDF assinado",
    icon: Clock, gradient: "from-slate-700 to-slate-900",
    testId: "admin-menu-afd",
  },
  {
    id: "supervisores", title: "Supervisores",
    subtitle: "Painel operacional dos supervisores",
    icon: ShieldCheck, gradient: "from-teal-600 to-emerald-700",
    testId: "admin-menu-supervisores",
  },
  {
    id: "aso", title: "Consulta de ASOs",
    subtitle: "Vencimento dos exames médicos",
    icon: Stethoscope, gradient: "from-emerald-600 to-green-700",
    testId: "admin-menu-aso",
  },
  {
    id: "codigo-etica", title: "Código de Ética e Conduta",
    subtitle: "Biblioteca de PDFs institucionais",
    icon: BookOpen, gradient: "from-emerald-600 to-teal-700",
    testId: "admin-menu-codigo-etica",
  },
];

const AdminMenuGrid = ({ onSelect }) => {
  const { token } = useAuth();
  const [asoAlert, setAsoAlert] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/admin/aso/list?status=all`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 20000,
      })
      .then((r) => {
        if (cancelled) return;
        const s = r.data?.stats || {};
        setAsoAlert({
          expired: s.expired || 0,
          soon: s.soon || 0,
          today: r.data?.today || "",
        });
      })
      .catch(() => { /* silent — banner just hides */ });
    return () => { cancelled = true; };
  }, [token]);

  return (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25 }}
    className="mb-6"
    data-testid="admin-menu-grid"
  >
    <div className="mb-4">
      <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
        Bem-vindo, Administrador
      </h2>
      <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
        Selecione uma área para começar.
      </p>
    </div>

    {asoAlert && (asoAlert.expired > 0 || asoAlert.soon > 0) && (
      <motion.button
        type="button"
        onClick={() => onSelect("aso")}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full text-left mb-4 sm:mb-5 rounded-xl border-2 border-red-200 bg-gradient-to-r from-red-50 via-white to-amber-50 hover:from-red-100 hover:to-amber-100 transition-colors shadow-sm p-3 sm:p-4"
        data-testid="admin-aso-alert-banner"
      >
        <div className="flex items-center gap-3">
          <div className="bg-red-600 text-white rounded-lg w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center flex-shrink-0 shadow-md">
            <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs sm:text-sm font-semibold text-slate-900 flex items-center gap-1.5">
              Atenção — Vencimento de ASOs
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs sm:text-sm">
              {asoAlert.expired > 0 && (
                <span className="inline-flex items-center gap-1 text-red-800 font-semibold">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-600" />
                  {asoAlert.expired} vencido{asoAlert.expired !== 1 ? "s" : ""}
                </span>
              )}
              {asoAlert.soon > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-800 font-semibold">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                  {asoAlert.soon} a vencer em ≤ 30 dias
                </span>
              )}
            </div>
          </div>
          <div className="hidden sm:flex items-center text-xs text-slate-500">
            Clique para ver a lista →
          </div>
        </div>
      </motion.button>
    )}

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
      {ADMIN_MENU_ITEMS.map((item, idx) => {
        const Icon = item.icon;
        return (
          <motion.button
            key={item.id}
            type="button"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.03, duration: 0.25 }}
            whileTap={{ scale: 0.985 }}
            onClick={() => onSelect(item.id)}
            data-testid={item.testId}
            className={`group text-left bg-gradient-to-br ${item.gradient} rounded-2xl p-4 sm:p-5 shadow-md hover:shadow-xl transition-shadow`}
          >
            <div className="flex items-center gap-3">
              <div className="bg-white/20 w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-base sm:text-lg leading-tight">
                  {item.title}
                </p>
                <p className="text-white/80 text-[11px] sm:text-xs mt-0.5 leading-snug">
                  {item.subtitle}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-white/70 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </motion.button>
        );
      })}
    </div>
  </motion.div>
  );
};

export default AdminDashboard;
